import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { usePendingOnboardingAssets } from '@/hooks/usePendingOnboardingAssets';
import { roleRouteAllows, type RouteRole } from '@/lib/routeRoleAccess';

interface RoleRouteProps {
  allowed: Array<RouteRole>;
  children: React.ReactNode;
  fallback?: string; // path
  /**
   * Route-shaped placeholder to paint while the gate is still deciding.
   * Without one the gate renders nothing, which is what used to leave the page
   * blank on entry (see `AutomationsPageSkeleton`).
   */
  skeleton?: React.ReactNode;
}

export function RoleRoute({ allowed, children, fallback = '/home/inbox', skeleton }: RoleRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isAutomationRoute = location.pathname.startsWith('/home/automation');

  // The onboarding assets only feed the automation bypass below, for a user
  // whose role is turned away. Fetched only then - every other guarded page
  // used to request them on each visit and never read the answer.
  const mayNeedOnboardingBypass =
    !loading && !!user && isAutomationRoute && !roleRouteAllows(user, allowed, location.pathname);
  const { data: pendingAssets, isLoading: pendingAssetsLoading } = usePendingOnboardingAssets({
    enabled: mayNeedOnboardingBypass,
  });
  const loadingView = <>{skeleton ?? null}</>;

  if (loading) return loadingView;
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // The onboarding-assets query only feeds the automation bypass below, which
  // applies when the role check says no. When the role already allows the
  // route there is nothing left to decide, so don't hold the page for it.
  if (roleRouteAllows(user, allowed, location.pathname)) {
    return <>{children}</>;
  }

  if (pendingAssetsLoading) return loadingView;

  const allowOnboardingAutomationAccess =
    isAutomationRoute && (!!pendingAssets?.pending_flow || !!pendingAssets?.activated_business_account_id);

  if (!allowOnboardingAutomationAccess) {
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
}
